import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { AlertTriangle, Shield, Eye, Camera, Monitor, Clock, User, FileText, Unlock, Image as ImageIcon, Video as VideoIcon, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { API } from '@/config/api';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';

const MonitorImage = ({ evidenceId, token, size = 'thumb' }) => {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!evidenceId) return;
    axios
      .get(`${API}/monitor/evidence/${evidenceId}/image`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      })
      .then((res) => {
        setSrc(URL.createObjectURL(res.data));
      })
      .catch(() => setError(true));
  }, [evidenceId, token]);

  if (error || !src) return null;

  const isLarge = size === 'large';
  return (
    <div
      className="flex items-center gap-3 p-3 rounded border"
      style={{ borderColor: '#E8F2FF', background: '#F5F9FF' }}
    >
      <img
        src={src}
        alt="Monitoring snapshot"
        style={isLarge ? {
          width: '100%',
          maxHeight: 220,
          objectFit: 'contain',
          borderRadius: 8,
          border: '2px solid #f97316'
        } : {
          width: 80,
          height: 80,
          objectFit: 'cover',
          borderRadius: 8,
          border: '2px solid #f97316'
        }}
      />
      {!isLarge && (
      <div>
        <p className="text-sm font-semibold" style={{ color: '#1a1a1a' }}>
          📷 Monitoring snapshot
        </p>
        <p className="text-xs" style={{ color: '#666' }}>
          Captured by AI monitoring system
        </p>
      </div>
      )}
    </div>
  );
};

export default function EvidenceVault({ token }) {
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [documents, setDocuments] = useState({});
  // Map evidence_id -> alert timestamp (ms) so Evidence Vault time matches Alerts exactly
  const [alertTimes, setAlertTimes] = useState({});

  useEffect(() => {
    fetchEvidence();
    fetchAlertTimes();
  }, []);

  useEffect(() => {
    // Listen for realtime security alerts and refresh evidence
    if (!token) return;

    const wsUrl = `ws://localhost:8001/ws/alerts?token=${token}`;
    const ws = new WebSocket(wsUrl);

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'security_alert') {
          console.log('[EvidenceVault] New security alert, refreshing...');
          fetchEvidence();
        }
      } catch (e) {
        // ignore parse errors
      }
    };

    ws.onerror = () => {
      // silent fail; Evidence Vault will still work via manual/interval refresh
    };

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [token]);

  useEffect(() => {
    // Periodic auto-refresh while Evidence Vault is open
    const interval = setInterval(() => {
      fetchEvidence();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  const fetchEvidence = async () => {
    try {
      // Get all documents first
      const docsResponse = await axios.get(`${API}/documents/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const docsMap = {};
      docsResponse.data.documents.forEach(doc => {
        docsMap[doc.udt_id] = doc;
      });
      setDocuments(docsMap);
      
      // Get all evidence for owned documents
      const udtIds = Object.keys(docsMap);
      const allEvidence = [];
      
      for (const udtId of udtIds) {
        try {
          const evidenceResponse = await axios.get(`${API}/misuse/evidence/${udtId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          allEvidence.push(...evidenceResponse.data.evidence);
        } catch (error) {
          // Skip if no evidence for this document
        }

        // Also fetch monitoring evidence (encrypted-at-rest, preview decrypted server-side for owner only)
        try {
          const monitorResponse = await axios.get(`${API}/monitor/evidence/${udtId}`, {
            params: { include_preview: true },
            headers: { Authorization: `Bearer ${token}` }
          });
          allEvidence.push(...(monitorResponse.data.evidence || []));
        } catch (error) {
          // Skip if no monitor evidence (or endpoint not available)
        }
      }

      console.log('[EV] Total evidence fetched:', allEvidence.length);

      // Deduplicate by evidence_id keeping newest
      const seen = new Map();
      for (const ev of allEvidence) {
        if (!ev?.evidence_id) continue;
        const existing = seen.get(ev.evidence_id);
        if (!existing) {
          seen.set(ev.evidence_id, ev);
        } else {
          const existingTime =
            existing.timestamp_ms || new Date(existing.timestamp || 0).getTime();
          const newTime =
            ev.timestamp_ms || new Date(ev.timestamp || 0).getTime();
          if (newTime > existingTime) {
            seen.set(ev.evidence_id, ev);
          }
        }
      }

      const dedupedEvidence = Array.from(seen.values());

      // Sort newest first using timestamp_ms / timestamp
      dedupedEvidence.sort((a, b) => {
        const ta = b.timestamp_ms || new Date(b.timestamp || 0).getTime();
        const tb = a.timestamp_ms || new Date(a.timestamp || 0).getTime();
        return ta - tb;
      });

      console.log(
        '[EV] After dedup:',
        dedupedEvidence.length,
        'Latest evidence:',
        dedupedEvidence[0]?.evidence_id,
        dedupedEvidence[0]?.timestamp
      );

      setEvidence(dedupedEvidence);
    } catch (error) {
      toast.error('Failed to fetch evidence');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlertTimes = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;
      const alertList = Array.isArray(data)
        ? data
        : (data.alerts || data.misuse_alerts || data.data || []);

      const map = {};
      alertList.forEach((a) => {
        if (!a?.evidence_id) return;
        const tsValue = a.created_at || a.timestamp || a.ts_ms || a.timestamp_ms;
        if (!tsValue) return;
        const d = new Date(tsValue);
        if (!Number.isNaN(d.getTime())) {
          map[a.evidence_id] = d.getTime();
        }
      });

      setAlertTimes(map);
    } catch {
      // If alerts fail, Evidence Vault still works with local timestamps
    }
  };

  const viewDetails = (ev) => {
    setSelectedEvidence(ev);
    setDetailDialog(true);
  };

  const downloadProofVideo = async (evidenceId, udtId) => {
    try {
      const res = await axios.get(`${API}/misuse/proof-video/${evidenceId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const blobUrl = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `UDT_${udtId || 'document'}_${evidenceId}.webm`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download proof video');
    }
  };

  const deleteEvidence = async (ev) => {
    try {
      if (!ev?.evidence_id) return;
      const ok = window.confirm('Delete this evidence? This will remove it from Evidence Vault, Alerts, and Audit Report.');
      if (!ok) return;

      const source = ev?.monitor ? 'monitor' : 'misuse';
      await axios.post(`${API}/evidence/delete`, {
        evidence_id: ev.evidence_id,
        source
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Evidence deleted');
      setEvidence((prev) => prev.filter((x) => x.evidence_id !== ev.evidence_id));
      setDetailDialog(false);
      setSelectedEvidence(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete evidence');
    }
  };

  const formatViolation = (type) => {
    if (!type) return 'Misuse detected';
    const map = {
      MULTIPLE_FACES: 'Multiple faces detected',
      PHONE_CAMERA_DETECTED: 'Phone camera detected',
      MOTION_BURST_NEAR_FACE: 'Motion detected near face',
      CAMERA_FROZEN_LOCAL: 'Camera frozen / covered',
      CAMERA_BLOCKED_LOCAL: 'Camera blocked / covered',
      PHONE_CAMERA_SUSPECTED: 'Phone camera detected',
      SCREEN_RECORDING: 'Screen recording detected',
      TAB_SWITCH: 'Tab switch detected',
      TAB_SWITCH_VIOLATION: 'Tab switch detected',
      EXCESSIVE_TAB_SWITCH: 'Excessive tab switching',
      KEYBOARD_SHORTCUT: 'Restricted keyboard shortcut',
      screenshot_attempt: 'Screenshot attempt',
      screenshot_detected: 'Screenshot detected',
      download_attempt: 'Unauthorized download attempt',
      print_attempt: 'Unauthorized print attempt',
      no_face: 'No face detected',
      NO_FACE_DETECTED: 'No face detected',
      phone_camera: 'Phone camera detected',
      camera_blocked: 'Camera blocked / covered',
      FACE_DISAPPEARED: 'Face disappeared',
      monitor_event: 'Monitoring event',
      system_error: 'AI monitoring error',
      UNKNOWN: 'Unknown violation'
    };
    return map[type] || String(type).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getActionTaken = (ev) => {
    const monitorAction = String(ev?.monitor?.action || '').toLowerCase();
    if (monitorAction === 'revoke') return 'Access revoked + keys rotated';
    if (monitorAction === 'lock') return 'Access locked (token revoked)';
    if (monitorAction === 'warn') return 'Warning issued';
    // Legacy misuse evidence implies revoke happened
    return 'Access revoked (security policy)';
  };

  const getIncidentReason = (ev) => {
    // Priority order: verdict reason (most specific) > monitor reason > proof message > revoked reason
    if (ev?.monitor?.verdict?.reason) return ev.monitor.verdict.reason;
    if (ev?.monitor?.reason) return ev.monitor.reason;
    if (ev?.proof_data?.message) return ev.proof_data.message;
    if (ev?.proof_data?.revoked_reason) return ev.proof_data.revoked_reason;

    return '';
  };

  const getEvidenceDate = (ev) => {
    if (!ev) return null;
    try {
      // 1) If this evidence has a matching alert, always use that timestamp
      if (alertTimes && ev.evidence_id && alertTimes[ev.evidence_id]) {
        return new Date(alertTimes[ev.evidence_id]);
      }

      // Prefer stable numeric timestamps used for alerts/evidence ordering
      if (ev.timestamp_ms) return new Date(ev.timestamp_ms);
      if (ev.ts_ms) return new Date(ev.ts_ms);
      if (ev.monitor?.timestamp_ms) return new Date(ev.monitor.timestamp_ms);

      // Fall back to ISO-like fields (actual incident time)
      const iso =
        ev.proof_data?.timestamp ||
        ev.monitor?.timestamp ||
        ev.timestamp ||
        ev.created_at;

      if (!iso) return null;
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  };

  const getDetailedLockReason = (ev) => {
    // Build comprehensive lock reason from all available sources
    const reasons = [];
    
    // Primary reason from verdict (most important - shows exact technical reason)
    if (ev?.monitor?.verdict?.reason) {
      const verdictReason = ev.monitor.verdict.reason;
      reasons.push(`🔍 Primary Reason: ${verdictReason}`);
      
      // Special handling for "Vision model not configured" - explain what this means
      if (verdictReason.includes('Vision model not configured') || verdictReason.includes('vision model')) {
        reasons.push(`⚠️ Note: OPENAI_API_KEY environment variable is not set. System is using fallback detection without AI vision analysis.`);
      }
    }
    
    // Action taken
    const action = ev?.monitor?.action || '';
    if (action) {
      const actionMap = {
        'lock': 'Document was locked (access temporarily revoked - requires re-verification)',
        'revoke': 'Access was permanently revoked (document keys rotated)',
        'warn': 'Warning was issued (monitoring continues)',
        'ignore': 'No action taken (event logged only)'
      };
      reasons.push(`⚡ Action Taken: ${actionMap[action] || action.toUpperCase()}`);
    }
    
    // Violation type
    if (ev?.violation_type) {
      reasons.push(`🚨 Violation Type: ${formatViolation(ev.violation_type)}`);
    }
    
    // Event type from monitor
    if (ev?.monitor?.event_type) {
      reasons.push(`📋 Event Type: ${String(ev.monitor.event_type).toUpperCase()}`);
    }
    
    // Risk score if available
    if (ev?.monitor?.risk?.risk_score != null) {
      const riskScore = Number(ev.monitor.risk.risk_score);
      const riskLevel = riskScore >= 80 ? 'HIGH' : riskScore >= 40 ? 'MEDIUM' : 'LOW';
      reasons.push(`📊 Risk Score: ${riskScore.toFixed(1)}/100 (${riskLevel} RISK)`);
      
      // Show breakdown if available
      if (ev?.monitor?.risk?.local_score != null) {
        reasons.push(`   └─ Local Detection Score: ${Number(ev.monitor.risk.local_score).toFixed(1)}`);
      }
      if (ev?.monitor?.risk?.llm_score != null) {
        reasons.push(`   └─ AI Vision Score: ${Number(ev.monitor.risk.llm_score).toFixed(1)}`);
      }
    }
    
    // Verdict class if available
    if (ev?.monitor?.verdict?.class) {
      const confidence = ev?.monitor?.verdict?.confidence != null 
        ? ` (${(Number(ev.monitor.verdict.confidence) * 100).toFixed(1)}% confidence)`
        : '';
      reasons.push(`🎯 Detection Class: ${ev.monitor.verdict.class}${confidence}`);
    }
    
    // Risk bucket
    if (ev?.monitor?.bucket) {
      reasons.push(`📦 Risk Bucket: ${String(ev.monitor.bucket).toUpperCase()}`);
    }
    
    // Additional context from proof data
    if (ev?.proof_data?.message && !ev?.monitor?.verdict?.reason) {
      reasons.push(`📝 Additional Context: ${ev.proof_data.message}`);
    }
    
    // Timestamp
    if (ev?.timestamp) {
      reasons.push(`⏰ Detected At: ${new Date(ev.timestamp).toLocaleString()}`);
    }
    
    return reasons.length > 0 ? reasons.join('\n') : 'No detailed reason available';
  };

  const allowAgain = async (ev) => {
    try {
      await axios.post(`${API}/access/allow-again`, {
        udt_id: ev.udt_id,
        recipient_id: ev.viewer_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Access restored for recipient');
      fetchEvidence();
      setDetailDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore access');
    }
  };

  const getViolationColor = (type) => {
    const colors = {
      'MULTIPLE_FACES': '#ef4444',
      'PHONE_CAMERA_DETECTED': '#f97316',
      'TAB_SWITCH': '#eab308',
      'EXCESSIVE_TAB_SWITCH': '#ef4444',
      'KEYBOARD_SHORTCUT': '#eab308',
      'MANUAL_TEST_TRIGGER': '#6366f1',
      'SCREEN_RECORDING': '#dc2626',
      'camera_blocked': '#dc2626',
    };
    return colors[type] || '#666';
  };

  const getViolationIcon = (type) => {
    switch(type) {
      case 'MULTIPLE_FACES':
      case 'PHONE_CAMERA_DETECTED':
        return <Camera className="w-5 h-5" />;
      case 'camera_blocked':
        return <Camera className="w-5 h-5" />;
      case 'SCREEN_RECORDING':
      case 'TAB_SWITCH':
      case 'EXCESSIVE_TAB_SWITCH':
        return <Monitor className="w-5 h-5" />;
      default:
        return <AlertTriangle className="w-5 h-5" />;
    }
  };

  const getProofImage = (ev) => {
    const face = ev?.proof_data?.face_image;
    const screenshot = ev?.proof_data?.screenshot;
    if (face && typeof face === 'string' && face.startsWith('data:image')) return { src: face, label: 'Face snapshot' };
    if (screenshot && typeof screenshot === 'string' && screenshot.startsWith('data:image')) return { src: screenshot, label: 'Screenshot' };
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 min-w-0">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-lg sm:text-2xl font-bold" style={{ color: '#1a1a1a' }}>Evidence Vault</h2>
          <p className="text-sm" style={{ color: '#666' }}>Security violations and proof records</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Badge variant="outline" className="text-lg px-4 py-2">
            Total: {evidence.length}
          </Badge>
          <Badge className="text-lg px-4 py-2" style={{ background: evidence.length > 0 ? '#ef4444' : '#10b981' }}>
            {evidence.length > 0 ? 'Violations Detected' : 'All Clear'}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchEvidence}
            disabled={loading}
          >
            🔄 Refresh
          </Button>
        </div>
      </div>

      {evidence.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Shield className="w-16 h-16 mx-auto mb-4" style={{ color: '#10b981' }} />
            <p className="font-semibold" style={{ color: '#10b981' }}>No Security Violations</p>
            <p className="text-sm mt-2" style={{ color: '#666' }}>All documents are secure with no detected misuse</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {evidence.map((ev) => {
            const doc = documents[ev.udt_id];
            
            return (
              <Card 
                key={`${ev.evidence_id}-${ev.timestamp_ms || ev.timestamp}`} 
                className="border-l-4 hover-lift" 
                style={{ borderLeftColor: getViolationColor(ev.violation_type) }}
                data-testid={`evidence-${ev.evidence_id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ background: `${getViolationColor(ev.violation_type)}20`, color: getViolationColor(ev.violation_type) }}
                      >
                        {getViolationIcon(ev.violation_type)}
                      </div>
                      <div>
                        <CardTitle className="text-lg" style={{ color: getViolationColor(ev.violation_type) }}>
                          {formatViolation(ev.violation_type)}
                        </CardTitle>
                        <CardDescription>
                          Evidence ID: {ev.evidence_id}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="outline">
                      {(getEvidenceDate(ev) || new Date()).toLocaleString()}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Document info */}
                  <div className="flex items-center gap-3 p-3 rounded" style={{ background: '#F5F9FF' }}>
                    <FileText className="w-5 h-5" style={{ color: '#0F52BA' }} />
                    <div className="flex-1">
                      <p className="font-semibold text-sm" style={{ color: '#1a1a1a' }}>
                        {doc?.file_name || 'Unknown Document'}
                      </p>
                      <p className="text-xs" style={{ color: '#666' }}>Document UDT ID: {ev.udt_id}</p>
                    </div>
                    <Badge className={doc?.status === 'locked' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}>
                      {doc?.status || 'unknown'}
                    </Badge>
                  </div>

                  {/* Misuse face snapshot (proof_data.face_image) */}
                  {/* {ev.proof_data?.face_image && (
                    <div
                      className="flex items-center gap-3 p-3 rounded border"
                      style={{ borderColor: '#E8F2FF', background: '#F5F9FF' }}
                     >
                      <img
                        src={ev.proof_data.face_image}
                        alt="Face captured at incident"
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: 'cover',
                          borderRadius: 8,
                          border: '2px solid #ef4444'
                        }}
                      />
                      <div>
                        <p className="text-sm font-semibold" style={{ color: '#1a1a1a' }}>
                          📸 Face captured at incident
                        </p>
                        <p className="text-xs" style={{ color: '#666' }}>
                          Webcam snapshot taken automatically
                        </p>
                      </div>
                    </div>
                  )} */}

                  {/* Monitor snapshot (encrypted monitor evidence image) */}
                  {ev.monitor &&
                    ev.monitor.mediapipe &&
                    Object.keys(ev.monitor.mediapipe).length > 0 && (
                      <MonitorImage evidenceId={ev.evidence_id} token={token} />
                    )}

                  {/* Detailed Lock Reason - Prominently Displayed */}
                  {getIncidentReason(ev) && (
                    <div className="p-4 rounded-lg border-2" style={{ 
                      background: '#FFF5F5', 
                      borderColor: getViolationColor(ev.violation_type),
                      borderLeftWidth: '4px'
                    }}>
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: getViolationColor(ev.violation_type) }} />
                        <div className="flex-1">
                          <p className="text-sm font-bold mb-1" style={{ color: getViolationColor(ev.violation_type) }}>
                            Why Document Was Locked:
                          </p>
                          <p className="text-sm font-semibold mb-2" style={{ color: '#1a1a1a' }}>
                            {getIncidentReason(ev)}
                          </p>
                          <details className="mt-2">
                            <summary className="text-xs cursor-pointer font-semibold" style={{ color: '#666' }}>
                              View Full Details
                            </summary>
                            <div className="mt-2 p-3 rounded text-xs" style={{ background: '#F9FAFB', color: '#555' }}>
                              {getDetailedLockReason(ev)}
                            </div>
                          </details>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Evidence details */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" style={{ color: '#666' }} />
                      <div>
                        <p className="text-xs" style={{ color: '#666' }}>Viewer (UDT User ID)</p>
                        <HoverCard>
                          <HoverCardTrigger asChild>
                            <button className="text-sm font-semibold text-left underline-offset-2 hover:underline font-mono">
                              {ev.viewer?.name || ev.viewer?.udt_user_id || ev.viewer_id || '—'}
                            </button>
                          </HoverCardTrigger>
                          <HoverCardContent className="w-80">
                            <div className="space-y-1">
                              {ev.viewer?.udt_user_id && <p className="font-semibold font-mono text-green-700">UDT User ID: {ev.viewer.udt_user_id}</p>}
                              <p className="text-sm">{ev.viewer?.name || 'Unknown viewer'}</p>
                              {ev.viewer?.email && <p className="text-xs text-gray-600 break-all">Email: {ev.viewer.email}</p>}
                              {ev.viewer?.mobile && <p className="text-xs text-gray-600 break-all">Mobile: {ev.viewer.mobile}</p>}
                              <p className="text-xs text-gray-500 break-all">Internal ID: {ev.viewer_id}</p>
                            </div>
                          </HoverCardContent>
                        </HoverCard>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" style={{ color: '#666' }} />
                      <div>
                        <p className="text-xs" style={{ color: '#666' }}>Time</p>
                        <p className="text-sm font-semibold">
                          {(getEvidenceDate(ev) || new Date()).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" style={{ color: '#666' }} />
                      <div>
                        <p className="text-xs" style={{ color: '#666' }}>Action Taken</p>
                        <p className="text-sm font-semibold">{getActionTaken(ev)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Proof indicators */}
                  <div className="flex items-center gap-3 p-3 rounded border" style={{ borderColor: '#E8F2FF' }}>
                    <Shield className="w-5 h-5" style={{ color: '#0F52BA' }} />
                    <div className="flex-1">
                      <p className="text-sm font-semibold" style={{ color: '#1a1a1a' }}>Proof Captured</p>
                      <div className="flex gap-2 mt-1">
                        {ev.proof_data?.face_image && (
                          <Badge variant="outline" className="text-xs">📸 Face Snapshot</Badge>
                        )}
                        {ev.proof_data?.screenshot && (
                          <Badge variant="outline" className="text-xs">🖼️ Screenshot</Badge>
                        )}
                        {ev.proof_data?.video_path && (
                          <Badge variant="outline" className="text-xs">🎥 Video</Badge>
                        )}
                        {ev.proof_data?.screen_hash && (
                          <Badge variant="outline" className="text-xs">🖥️ Screen Hash</Badge>
                        )}
                        {ev.monitor?.verdict?.class && (
                          <Badge variant="outline" className="text-xs">🧠 {ev.monitor.verdict.class}</Badge>
                        )}
                        <Badge variant="outline" className="text-xs">⏱️ Timestamp</Badge>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => viewDetails(ev)}
                      style={{ background: '#0F52BA' }}
                      data-testid={`view-evidence-${ev.evidence_id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Evidence Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">Evidence Details</DialogTitle>
          </DialogHeader>
          {selectedEvidence && (
            <div className="space-y-4">
              {/* Incident Summary */}
              <div className="p-4 rounded-lg border" style={{ background: '#F5F9FF', borderColor: '#E8F2FF' }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: `${getViolationColor(selectedEvidence.violation_type)}20`, color: getViolationColor(selectedEvidence.violation_type) }}
                    >
                      {getViolationIcon(selectedEvidence.violation_type)}
                    </div>
                    <div>
                      <p className="text-xs font-semibold" style={{ color: '#666' }}>Incident</p>
                      <p className="text-lg font-bold" style={{ color: getViolationColor(selectedEvidence.violation_type) }}>
                        {formatViolation(selectedEvidence.violation_type)}
                      </p>
                      {selectedEvidence?.monitor && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedEvidence.monitor.action && <Badge variant="outline">Action: {String(selectedEvidence.monitor.action).toUpperCase()}</Badge>}
                          {selectedEvidence.monitor.bucket && <Badge variant="outline">{String(selectedEvidence.monitor.bucket)}</Badge>}
                          {selectedEvidence.monitor.verdict?.class && <Badge variant="outline">Verdict: {selectedEvidence.monitor.verdict.class}</Badge>}
                          {selectedEvidence.monitor.risk?.risk_score != null && (
                            <Badge variant="outline">Risk: {Number(selectedEvidence.monitor.risk.risk_score).toFixed(2)}</Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <Badge variant="outline">
                    {(getEvidenceDate(selectedEvidence) || new Date()).toLocaleString()}
                  </Badge>
                </div>
              </div>

              {/* Detailed Lock Reason - Prominently Displayed in Dialog */}
              {getIncidentReason(selectedEvidence) && (
                <div className="p-4 rounded-lg border-2" style={{ 
                  background: '#FFF5F5', 
                  borderColor: getViolationColor(selectedEvidence.violation_type),
                  borderLeftWidth: '4px'
                }}>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: getViolationColor(selectedEvidence.violation_type) }} />
                    <div className="flex-1">
                      <p className="text-base font-bold mb-2" style={{ color: getViolationColor(selectedEvidence.violation_type) }}>
                        🔒 Exact Reason Document Was Locked:
                      </p>
                      <p className="text-base font-semibold mb-3 p-3 rounded" style={{ background: '#FFFFFF', color: '#1a1a1a', border: '1px solid #E5E7EB' }}>
                        {getIncidentReason(selectedEvidence)}
                      </p>
                      <details className="mt-2" open>
                        <summary className="text-sm cursor-pointer font-semibold mb-2" style={{ color: '#666' }}>
                          📋 Complete Technical Details
                        </summary>
                        <div className="mt-2 p-4 rounded text-sm font-mono" style={{ background: '#F9FAFB', color: '#555', border: '1px solid #E5E7EB' }}>
                          {getDetailedLockReason(selectedEvidence)}
                        </div>
                      </details>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="font-semibold mb-2 text-sm">Evidence ID</p>
                  <code className="text-xs sm:text-sm px-3 py-2 rounded block break-all" style={{ background: '#f3f4f6' }}>
                    {selectedEvidence.evidence_id}
                  </code>
                </div>
                
                <div>
                  <p className="font-semibold mb-2 text-sm">Timestamp</p>
                  <p className="text-sm">
                    {(getEvidenceDate(selectedEvidence) || new Date()).toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="font-semibold mb-2 text-sm">Action taken</p>
                  <p className="text-sm">{getActionTaken(selectedEvidence)}</p>
                </div>
                <div>
                  <p className="font-semibold mb-2 text-sm">Document</p>
                  <p className="text-sm">
                    {documents?.[selectedEvidence.udt_id]?.file_name || selectedEvidence?.document?.file_name || selectedEvidence.udt_id}
                  </p>
                </div>
              </div>
              
              <div>
                <p className="font-semibold mb-2 text-sm flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Viewer (one UDT User ID per person, consistent everywhere)
                </p>
                <div className="p-3 rounded border" style={{ borderColor: '#E8F2FF', background: '#F5F9FF' }}>
                  {selectedEvidence.viewer?.udt_user_id && (
                    <p className="text-sm font-semibold font-mono text-green-700 mb-1">UDT User ID: {selectedEvidence.viewer.udt_user_id}</p>
                  )}
                  <p className="text-sm font-semibold">{selectedEvidence.viewer?.name || 'Unknown viewer'}</p>
                  {selectedEvidence.viewer?.email && <p className="text-xs text-gray-600 break-all">Email: {selectedEvidence.viewer.email}</p>}
                  {selectedEvidence.viewer?.mobile && <p className="text-xs text-gray-600 break-all">Mobile: {selectedEvidence.viewer.mobile}</p>}
                  <p className="text-xs text-gray-500 font-mono break-all mt-1">Internal ID: {selectedEvidence.viewer_id}</p>
                </div>
              </div>

              {/* Video evidence indicator (encrypted at rest) */}
              {selectedEvidence.proof_data?.video_path && (
                <div className="p-3 rounded border flex items-start gap-3" style={{ borderColor: '#E8F2FF', background: '#F5F9FF' }}>
                  <VideoIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#0F52BA' }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold" style={{ color: '#666' }}>Proof video recorded: ✓</p>
                    <p className="text-xs mt-1 break-all font-mono" style={{ color: '#999' }}>
                      Stored encrypted on server
                    </p>
                    <div className="mt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadProofVideo(selectedEvidence.evidence_id, selectedEvidence.udt_id)}
                      >
                        <VideoIcon className="w-4 h-4 mr-2" />
                        Download proof video
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <p className="font-semibold mb-2 text-sm flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Proof Data
                </p>
                <div className="space-y-2">
                  {selectedEvidence.proof_data?.face_image ? (
                    <div className="p-3 rounded border flex items-start gap-3" style={{ borderColor: '#E8F2FF', background: '#F5F9FF' }}>
                      <Camera className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#0F52BA' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold" style={{ color: '#666' }}>Face snapshot: ✓</p>
                        <p className="text-xs mt-1" style={{ color: '#999' }}>
                          Captured from viewer webcam at incident time.
                        </p>
                        <div className="mt-2 rounded overflow-hidden border" style={{ borderColor: '#E5E7EB' }}>
                          <img
                            src={selectedEvidence.proof_data.face_image}
                            alt="Face snapshot proof"
                            className="w-full"
                            style={{ maxHeight: 220, objectFit: 'contain' }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : selectedEvidence.monitor && selectedEvidence.evidence_id && (
                    <div className="p-3 rounded border flex items-start gap-3" style={{ borderColor: '#E8F2FF', background: '#F5F9FF' }}>
                      <Camera className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#0F52BA' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold" style={{ color: '#666' }}>Face snapshot (proof)</p>
                        <p className="text-xs mt-1" style={{ color: '#999' }}>
                          Captured from viewer webcam at incident time.
                        </p>
                        <div className="mt-2">
                          <MonitorImage evidenceId={selectedEvidence.evidence_id} token={token} size="large" />
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedEvidence.proof_data?.screen_hash && (
                    <div className="p-3 rounded border flex items-start gap-3" style={{ borderColor: '#E8F2FF', background: '#F5F9FF' }}>
                      <Monitor className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#0F52BA' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold" style={{ color: '#666' }}>Screen Hash: ✓</p>
                        <p className="text-xs mt-1 break-all font-mono" style={{ color: '#999' }}>{selectedEvidence.proof_data.screen_hash}</p>
                      </div>
                    </div>
                  )}
                  {selectedEvidence.proof_data?.page_url && (
                    <div className="p-3 rounded border flex items-start gap-3" style={{ borderColor: '#E8F2FF', background: '#F5F9FF' }}>
                      <Monitor className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#0F52BA' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold" style={{ color: '#666' }}>Page URL</p>
                        <p className="text-xs mt-1 break-all" style={{ color: '#999' }}>{selectedEvidence.proof_data.page_url}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="p-4 rounded" style={{ background: '#E8F2FF' }}>
                <p className="text-sm" style={{ color: '#0F52BA' }}>
                  <Shield className="w-4 h-4 inline mr-2" />
                  This evidence is stored securely and can be used for audit purposes.
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDetailDialog(false)}
                >
                  Close
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => deleteEvidence(selectedEvidence)}
                >
                  Delete evidence
                </Button>
                {selectedEvidence.udt_id && selectedEvidence.viewer_id && (
                  <Button
                    onClick={() => allowAgain(selectedEvidence)}
                    style={{ background: '#10b981' }}
                  >
                    <Unlock className="w-4 h-4 mr-2" />
                    Allow again
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
